/**
 * Encapsulates the full dockview lifecycle for the workbench layout (架构方案
 * §5.7): restore/build, capability re-application, root-edge dnd gating,
 * debounced persistence, visibility toggle, reset, and module-driven default
 * visibility — assembled into the `WorkbenchLayoutApi` served over Context.
 */
import type { LayoutArea, LayoutSchema } from '@hibro/shared';
import type {
  DockviewApi,
  DockviewReadyEvent,
  SerializedDockview,
} from 'dockview-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type ToggleableArea,
  type WorkbenchLayoutApi,
} from '../layout-context';
import { applyAreaViewClasses, findAreaChromeEl } from './area-classes';
import {
  AREA_DROP_POSITIONS,
  DEFAULT_AREA_SIZE,
  DRAG_LOCKED_AREAS,
  DROP_GATED_AREAS,
  type EdgePosition,
  isToggleableArea,
  LAYOUT_STORAGE_KEY,
  ROOT_DOCK_EDGES,
  SIZE_CHANGING_MUTATIONS,
} from './area-specs';
import { beginDockviewNativeContentDrag } from './dockview-drag-native';
import {
  addAreaPanel,
  applyTabStripVisibility,
  areaNodeExists,
  configureAreas,
  initDefaultLayout,
  isAreaVisibleNow,
  pinMutationPreservedAreaSizes,
  type PreservedAreaSizes,
  reconcileEnabledAreas,
  relayoutFromShell,
  repinEdgeAreas,
  restoreMutationPreservedAreaSizes,
  setAreaVisible,
} from './layout-builder';
import { installOverflowPopoverAnchor } from './overflow-popover';
import {
  applyRailIdleConstraints,
  installRailSnap,
  snapRailAreaSize,
} from './rail-snap';
import { resolveAuxiliaryBarResponsiveAction } from './responsive-areas';
import { findAreaConfig, isAreaEnabled } from './schema';
import {
  dropEditorInstancePanels,
  stripEditorInstanceTabs,
} from './strip-editor-instance-tabs';

/**
 * Encapsulates the full dockview lifecycle for the workbench layout.
 *
 * Responsibilities:
 *   - restore or build the default layout on dockview ready
 *   - re-apply per-area capabilities: drag / resize / drop (not serialized)
 *   - gate root (outer-edge) docking to {@link ROOT_DOCK_EDGES} directions
 *   - persist split positions to localStorage (debounced, 250 ms)
 *   - expose `toggle` for collapsible areas, preserving user size/position
 *     (VS Code style — dockview caches the invisible size)
 *   - hide auxiliaryBar below 800px and restore it from 900px, without
 *     persisting responsive visibility or overriding a user-hidden area
 *   - expose `reset` to discard customisation and rebuild the schema default
 *   - assemble and return a `WorkbenchLayoutApi` for the Context
 *
 * Future dockview operations (addTab, focusArea, maximize, …) belong here.
 *
 * Module-driven default visibility (§5.7): the active module may declare a
 * `hiddenAreas` blacklist. This is applied as a *soft* default layered on top of
 * the user's global visibility — when the active module changes, the toggleable
 * areas that module lists are hidden, and areas the *previous* module hid but
 * this one does not are restored. Only areas hidden by a module are ever
 * force-restored, so a user's manual close of an unrelated area is preserved.
 * The user can still reveal a module-hidden area via its header toggle (which
 * drops it from the module layer). Visibility is a single global state — there
 * is no per-module memory, matching the "area is global" decision.
 *
 * @param schema                    Effective layout schema (caller already
 *                                  applied occupancy overlays onto `enabled`).
 * @param activeModuleId            The active module (owner of the active view),
 *                                  the trigger for re-seeding module defaults.
 * @param activeModuleHiddenAreas   Areas that module wants hidden by default.
 * @param containerRef               Dockview root container measured by the
 *                                  workbench-wide responsive visibility policy.
 * @param contributionsReady        When false, restore/build is deferred so
 *                                  occupancy overlays (e.g. auxiliaryBar) see
 *                                  the real contributed view set instead of the
 *                                  empty first paint.
 */
export function useDockviewManager(
  schema: LayoutSchema,
  activeModuleId: string | undefined,
  activeModuleHiddenAreas: readonly LayoutArea[],
  containerRef: { readonly current: HTMLElement | null },
  contributionsReady = true,
): {
  onReady: (event: DockviewReadyEvent) => void;
  layoutApi: WorkbenchLayoutApi;
} {
  const apiRef = useRef<DockviewApi | undefined>(undefined);
  // Bumped on each reset and on unmount so a settled-shell relayout from an
  // older reset cannot run against a newer tree or a disposed api.
  const shellRelayoutGenerationRef = useRef(0);
  const layoutAppliedRef = useRef(false);
  const [hostReady, setHostReady] = useState(false);
  const saveDisposableRef = useRef<{ dispose(): void } | undefined>(undefined);
  const dragVetoDisposableRef = useRef<{ dispose(): void } | undefined>(
    undefined,
  );
  const overlayDisposableRef = useRef<{ dispose(): void } | undefined>(
    undefined,
  );
  const structuralSizeDisposableRef = useRef<{ dispose(): void } | undefined>(
    undefined,
  );
  const railSnapDisposableRef = useRef<{ dispose(): void } | undefined>(
    undefined,
  );
  const overflowPopoverDisposableRef = useRef<{ dispose(): void } | undefined>(
    undefined,
  );
  const preservedAreaSizesRef = useRef<PreservedAreaSizes | undefined>(
    undefined,
  );
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  /** Layout JSON waiting on the debounce. Flushed on page hide so a refresh
   * does not restore the tree from before the last close. */
  const pendingLayoutRef = useRef<string | undefined>(undefined);
  const responsiveAuxiliaryHiddenRef = useRef(false);
  const responsiveLayoutMutationRef = useRef(false);

  const [openAreas, setOpenAreas] = useState<Record<ToggleableArea, boolean>>({
    panel: false,
    statusBar: false,
    auxiliaryBar: false,
  });

  // Flips true once restore/build has run (after dockview host is ready *and*
  // contributions have settled), so the module-default effect only runs against
  // a live layout — and re-runs when it first becomes ready to seed the initial
  // active module's defaults.
  const [ready, setReady] = useState(false);

  // Latest active-module hidden areas, read inside the module-default effect
  // without making the effect re-run on every render (the array identity churns
  // each render even when the module is unchanged).
  const hiddenAreasRef = useRef<readonly LayoutArea[]>(activeModuleHiddenAreas);
  hiddenAreasRef.current = activeModuleHiddenAreas;

  // Stable content key for the active module's hidden set, so the module-default
  // effect re-runs when the *set changes* — even if `activeModuleId` is unchanged
  // (e.g. the async `listContributions` result arrives after the module is
  // already active, or a runtime contributions refresh changes the declaration).
  const hiddenKey = [...activeModuleHiddenAreas].sort().join('|');

  // The toggleable areas currently forced hidden by the active module. Only these
  // are ever force-restored on a module switch, so an unrelated user-closed area
  // stays closed. A user reveal (toggle) drops the area from this set.
  const moduleHiddenRef = useRef<Set<ToggleableArea>>(new Set());

  const sizeOf = useCallback(
    (area: LayoutArea): number =>
      findAreaConfig(schema, area)?.size ?? DEFAULT_AREA_SIZE,
    [schema],
  );

  // Mirror the live dockview visibility into `openAreas` (toggle button state).
  const syncOpenAreas = useCallback((api: DockviewApi) => {
    const next = {
      panel: isAreaVisibleNow(api, 'panel'),
      statusBar: isAreaVisibleNow(api, 'statusBar'),
      auxiliaryBar: isAreaVisibleNow(api, 'auxiliaryBar'),
    };
    setOpenAreas((current) =>
      current.panel === next.panel &&
      current.statusBar === next.statusBar &&
      current.auxiliaryBar === next.auxiliaryBar
        ? current
        : next,
    );
  }, []);

  /**
   * Toggle an area's visibility while preserving the user's last size and
   * position (VS Code style). One flow for both area kinds — edge vs central
   * grid is hidden behind {@link setAreaVisible} / {@link isAreaVisibleNow}:
   *   • Disabled by schema (`enabled: false`) → no-op. The area does not exist
   *     in this build and must never be created (架构方案 §5.7).
   *   • Node missing (migrated/partial layout) → build it once, shown.
   *   • Node present → flip visibility via the shared helper. dockview caches
   *     the invisible size (`Sizing.Invisible` / edge-group expanded size) and
   *     serialises it, so size + position survive hide→show and reloads.
   *
   * Capabilities are re-applied after re-showing (the locks / pinned sizes are
   * not serialised), then the toggle-button mirror state is updated.
   */
  const toggleArea = useCallback(
    (area: ToggleableArea) => {
      const api = apiRef.current;
      if (!api) {
        return;
      }
      // Schema-disabled areas are not toggleable.
      if (!isAreaEnabled(findAreaConfig(schema, area))) {
        return;
      }
      if (area === 'auxiliaryBar') {
        // A direct user toggle takes ownership away from responsive policy.
        // Widening must never undo a user-selected visibility state.
        responsiveAuxiliaryHiddenRef.current = false;
      }
      if (!areaNodeExists(api, area)) {
        // Absent from a migrated/partial layout — create it once (shown).
        addAreaPanel(api, area, sizeOf);
        configureAreas(api, schema, sizeOf);
        applyRailIdleConstraints(api);
        moduleHiddenRef.current.delete(area);
        setOpenAreas((prev) => ({ ...prev, [area]: true }));
        return;
      }
      const next = !isAreaVisibleNow(api, area);
      setAreaVisible(api, area, next);
      // Re-apply capabilities after re-showing (locks / pinned size, e.g. statusBar).
      if (next) {
        configureAreas(api, schema, sizeOf);
        applyRailIdleConstraints(api);
      }
      // A user reveal overrides the active module's default-hide, so drop it from
      // the module layer — it won't be re-hidden until the module changes again.
      if (next) {
        moduleHiddenRef.current.delete(area);
      }
      setOpenAreas((prev) => ({ ...prev, [area]: next }));
    },
    [schema, sizeOf],
  );

  const updateResponsiveAreas = useCallback((): void => {
    const api = apiRef.current;
    const container = containerRef.current;
    if (!api || !container || !ready) {
      return;
    }

    const auxiliaryConfig = findAreaConfig(schema, 'auxiliaryBar');
    if (!isAreaEnabled(auxiliaryConfig)) {
      responsiveAuxiliaryHiddenRef.current = false;
      return;
    }

    const action = resolveAuxiliaryBarResponsiveAction(
      container.getBoundingClientRect().width,
      responsiveAuxiliaryHiddenRef.current,
    );
    if (action === 'none') {
      return;
    }

    if (action === 'hide') {
      if (!isAreaVisibleNow(api, 'auxiliaryBar')) {
        // The user (or another policy layer) already hid it; do not claim
        // ownership or restore it when the workbench grows.
        return;
      }
      responsiveAuxiliaryHiddenRef.current = true;
      responsiveLayoutMutationRef.current = true;
      try {
        setAreaVisible(api, 'auxiliaryBar', false);
      } finally {
        responsiveLayoutMutationRef.current = false;
      }
      syncOpenAreas(api);
      return;
    }

    responsiveLayoutMutationRef.current = true;
    try {
      if (!areaNodeExists(api, 'auxiliaryBar')) {
        addAreaPanel(api, 'auxiliaryBar', sizeOf);
      } else {
        setAreaVisible(api, 'auxiliaryBar', true);
      }
      configureAreas(api, schema, sizeOf);
      applyRailIdleConstraints(api);
    } finally {
      responsiveLayoutMutationRef.current = false;
      responsiveAuxiliaryHiddenRef.current = false;
    }
    syncOpenAreas(api);
  }, [containerRef, ready, schema, sizeOf, syncOpenAreas]);

  /**
   * Discard the user's customised layout and rebuild the schema default. Clears
   * the persisted layout up-front (so a crash before the debounced save still
   * boots clean), rebuilds in place, then re-syncs the toggle state. The rebuild
   * fires `onDidLayoutChange`, which re-persists the fresh default (架构方案
   * §5.7).
   *
   * `initDefaultLayout` does a hard `api.clear()`, so the tabbed area's instance
   * tabs (e.g. AI session tabs) are wiped along with the rest of the tree — the
   * sessions themselves survive (business data owned by the module), only their
   * tab placement is layout state. A renderer-side module controller decides
   * whether and how to seed the rebuilt area.
   */
  const resetLayout = useCallback(() => {
    const api = apiRef.current;
    if (!api) {
      return;
    }
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(LAYOUT_STORAGE_KEY);
    }
    // Reset is an explicit user action. Drop stale responsive ownership, rebuild
    // the schema default, then immediately re-apply the current width policy so
    // a reset performed below 800px cannot leave auxiliaryBar visible.
    responsiveAuxiliaryHiddenRef.current = false;
    initDefaultLayout(api, schema, sizeOf);
    snapRailAreaSize(api);
    syncOpenAreas(api);
    updateResponsiveAreas();
    // `renderer: 'always'` overlays are measured on a later frame, and only
    // while visible. Two frames let that first write land, then remeasure the
    // shell so a 0 box captured during `clear()` cannot stick.
    const generation = ++shellRelayoutGenerationRef.current;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (
          generation !== shellRelayoutGenerationRef.current ||
          apiRef.current !== api
        ) {
          return;
        }
        relayoutFromShell(api);
      });
    });
  }, [schema, sizeOf, syncOpenAreas, updateResponsiveAreas]);

  /**
   * Apply the active module's default-hide blacklist as a soft layer over the
   * global visibility (see the hook doc). Runs once the live layout exists
   * (`ready`) and on every active-module change; the current hidden list is read
   * via `hiddenAreasRef` so unrelated re-renders don't re-run it.
   *
   * `moduleHiddenRef` tracks only areas *this layer* hid, so:
   *   - a currently-visible area the module lists is hidden and taken ownership of;
   *   - an area the user had already hidden is left untouched (not owned), so it
   *     is never force-shown later;
   *   - an owned area the new module no longer lists is restored (shown).
   */
  useEffect(() => {
    const api = apiRef.current;
    if (!api || !ready) {
      return;
    }
    const desired = new Set<ToggleableArea>();
    for (const area of hiddenAreasRef.current) {
      if (
        isToggleableArea(area) &&
        area !== 'auxiliaryBar' &&
        isAreaEnabled(findAreaConfig(schema, area))
      ) {
        desired.add(area);
      }
    }
    const prev = moduleHiddenRef.current;
    const nextOwned = new Set<ToggleableArea>();
    let changed = false;

    // Hide (and take ownership of) areas the active module wants hidden — but
    // only ones currently visible or already owned, so a user's manual hide of a
    // non-owned area is never claimed.
    for (const area of desired) {
      const visible = isAreaVisibleNow(api, area);
      if (prev.has(area) || visible) {
        if (visible) {
          setAreaVisible(api, area, false);
          changed = true;
        }
        nextOwned.add(area);
      }
    }
    // Restore areas the previous module owned but the current one does not.
    for (const area of prev) {
      if (nextOwned.has(area)) {
        continue;
      }
      if (!areaNodeExists(api, area)) {
        addAreaPanel(api, area, sizeOf);
      } else {
        setAreaVisible(api, area, true);
      }
      changed = true;
    }
    if (changed) {
      configureAreas(api, schema, sizeOf);
      applyRailIdleConstraints(api);
    }
    moduleHiddenRef.current = nextOwned;
    syncOpenAreas(api);
  }, [ready, activeModuleId, hiddenKey, schema, sizeOf, syncOpenAreas]);

  const applyLayout = useCallback(
    (api: DockviewApi) => {
      const storage =
        typeof window !== 'undefined' ? window.localStorage : undefined;
      let restored = false;
      const saved = storage?.getItem(LAYOUT_STORAGE_KEY);
      if (saved) {
        try {
          api.fromJSON(JSON.parse(saved) as SerializedDockview);
          restored = true;
        } catch {
          restored = false;
        }
      }
      // A tree without the `editor` anchor is not a usable layout: that panel
      // hosts the entire editor area, and nothing recreates it (`reconcile`
      // deliberately skips `editor`), so keeping such a tree would leave the
      // editor permanently blank — no rail click could bring it back. Fail closed
      // to the default build instead. `initDefaultLayout` clears first, so the
      // half-restored tree does not survive.
      if (restored && !api.getPanel('editor')) {
        restored = false;
      }
      if (restored) {
        dropEditorInstancePanels(api);
        repinEdgeAreas(api, sizeOf);
        configureAreas(api, schema, sizeOf);
      } else {
        initDefaultLayout(api, schema, sizeOf);
      }
      if (reconcileEnabledAreas(api, schema, sizeOf)) {
        configureAreas(api, schema, sizeOf);
      }
      // Magnetic rail: a leftover width snaps to the nearer of the two stops.
      snapRailAreaSize(api);
      syncOpenAreas(api);
    },
    [schema, sizeOf, syncOpenAreas],
  );

  useEffect(() => {
    const api = apiRef.current;
    if (!api || !hostReady || !contributionsReady || layoutAppliedRef.current) {
      return;
    }
    applyLayout(api);
    layoutAppliedRef.current = true;
    setReady(true);
  }, [hostReady, contributionsReady, applyLayout]);

  useEffect(() => {
    const api = apiRef.current;
    if (!api || !ready) {
      return;
    }
    if (reconcileEnabledAreas(api, schema, sizeOf)) {
      configureAreas(api, schema, sizeOf);
      snapRailAreaSize(api);
      syncOpenAreas(api);
    }
  }, [ready, schema, sizeOf, syncOpenAreas]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !ready) {
      return;
    }

    updateResponsiveAreas();
    const observer = new ResizeObserver(() => updateResponsiveAreas());
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, ready, updateResponsiveAreas]);

  const onReady = useCallback(
    (event: DockviewReadyEvent) => {
      const api = event.api;
      apiRef.current = api;
      const storage =
        typeof window !== 'undefined' ? window.localStorage : undefined;
      setHostReady(true);

      // Docking gates (架构方案 §5.7), both by vetoing unwanted overlays:
      //   1. Root (outer-edge) docking — dockview's `dndEdges` toggles all four
      //      root edges at once, so we keep them on and veto directions outside
      //      ROOT_DOCK_EDGES (currently left/right).
      //   2. Per-area content docking — a group belonging to a gated area
      //      (editor and auxiliaryBar: four split directions plus center)
      //      rejects any content overlay outside its allow-set. Tab-strip drops
      //      (kind 'tab' / 'header_space') stay untouched, so tab
      //      reordering/insertion works.
      overlayDisposableRef.current = api.onWillShowOverlay((e) => {
        if (e.kind === 'edge') {
          if (!ROOT_DOCK_EDGES.has(e.position as EdgePosition)) {
            e.preventDefault();
          }
          return;
        }
        if (e.kind === 'content' && e.group) {
          // Map the hovered group back to its area via anchor identity, then
          // enforce that area's allowed directions (undefined area = ungated).
          const gatedArea = DROP_GATED_AREAS.find(
            (area) => api.getPanel(area)?.group === e.group,
          );
          const allowed = gatedArea && AREA_DROP_POSITIONS[gatedArea];
          if (allowed && !allowed.has(e.position)) {
            e.preventDefault();
          }
        }
      });

      // Lock drag on non-draggable areas. A locked area whose strip is shown
      // (`showHeader`, currently `editor`) still exposes dockview's two native
      // drag handles — its anchor tab and the strip's empty space — so the
      // gesture has to be refused at its source.
      //
      // Refusing it from a **capture-phase** `dragstart` listener instead of
      // from `onWillDragPanel` / `onWillDragGroup` is load-bearing, not a
      // style choice. Those events fire from inside dockview's own
      // target-phase `dragstart` handler, which by then has already written
      // `pointer-events: none` inline onto every `<iframe>` in the document —
      // its shield against iframes swallowing `pointermove` mid-drag — and
      // releases them on `dragend`. A cancelled `dragstart` never produces a
      // `dragend`, so vetoing from there left every module iframe dead to the
      // wheel until a reload (symptom: an open document renders fine but will
      // not scroll). It could not even self-heal: the shield saves the current
      // inline value and restores it, so the next successful drag saved `none`
      // as the original. Capturing first means dockview reads
      // `event.defaultPrevented` on entry and declines the drag itself, before
      // any shield exists — hence no `stopPropagation`, its own guard is the
      // mechanism we rely on. Listening on the document (rather than the
      // dockview container) matches the shield's own scope and keeps the
      // handler free of a "container not mounted yet" branch.
      //
      // This covers the whole mouse path. dockview's pointer backend is
      // touch-only on a fine-pointer device, and it ignores `preventDefault()`
      // regardless (`PointerDragSource._beginDrag` begins the drag
      // unconditionally), so the old veto never gated it either; it also
      // cannot leak, releasing its shield from `pointerup` / `pointercancel`.
      const vetoLockedAreaDrag = (event: DragEvent): void => {
        if (!(event.target instanceof Element)) {
          return;
        }
        const handle = event.target.closest('.dv-tab, .dv-void-container');
        if (!handle) {
          return;
        }
        const locked = DRAG_LOCKED_AREAS.some((area) =>
          findAreaChromeEl(api, area)?.contains(handle),
        );
        // What a locked area forbids is dragging *the area* away: its
        // structural anchor tab, and a group drag off the strip's empty space.
        // A view-instance tab is one of its documents, not the area, and stays
        // draggable into another tabbed area (§5.7 / 见 20 八).
        const instanceTab =
          handle.classList.contains('dv-tab') &&
          !handle.querySelector('[data-app-area-anchor]');
        if (!locked || instanceTab) {
          return;
        }
        event.preventDefault();
      };
      document.addEventListener('dragstart', vetoLockedAreaDrag, true);
      // After the veto, so a cancelled drag is already `defaultPrevented`.
      document.addEventListener(
        'dragstart',
        beginDockviewNativeContentDrag,
        true,
      );
      dragVetoDisposableRef.current = {
        dispose: () => {
          document.removeEventListener('dragstart', vetoLockedAreaDrag, true);
          document.removeEventListener(
            'dragstart',
            beginDockviewNativeContentDrag,
            true,
          );
        },
      };

      // Root-edge drops need an earlier snapshot than the mutation boundary:
      // dockview creates the root target group in `orthogonalize()` before
      // entering `moveGroupOrPanel()` and firing `onWillMutateLayout`. That
      // target creation can already redistribute the root split (activityBar
      // would expand to its max), so capture/pin while `onWillDrop` still
      // sees the pre-drop geometry. `getData()` excludes external drops that do
      // not perform an internal layout mutation.
      const beforeRootDrop = api.onWillDrop((e) => {
        if (
          e.kind !== 'edge' ||
          !ROOT_DOCK_EDGES.has(e.position as EdgePosition) ||
          !e.getData() ||
          preservedAreaSizesRef.current !== undefined
        ) {
          return;
        }
        preservedAreaSizesRef.current = pinMutationPreservedAreaSizes(api);
      });

      // Preserve every area's configured cross-axis size while dockview mutates
      // the grid topology. Root-edge drops arrive here with the earlier
      // `onWillDrop` snapshot already installed; all other structural mutations
      // take their snapshot at the normal transaction boundary. Pins exist only
      // for the transaction, so sash resizing remains available afterwards.
      const beforeStructuralChange = api.onWillMutateLayout((e) => {
        if (
          !SIZE_CHANGING_MUTATIONS.has(e.kind) ||
          preservedAreaSizesRef.current !== undefined
        ) {
          return;
        }
        preservedAreaSizesRef.current = pinMutationPreservedAreaSizes(api);
      });
      const afterStructuralChange = api.onDidMutateLayout((e) => {
        if (!SIZE_CHANGING_MUTATIONS.has(e.kind)) {
          return;
        }
        const preserved = preservedAreaSizesRef.current;
        if (!preserved) {
          return;
        }
        preservedAreaSizesRef.current = undefined;
        restoreMutationPreservedAreaSizes(api, preserved);
        applyRailIdleConstraints(api);
      });
      structuralSizeDisposableRef.current = {
        dispose: () => {
          beforeRootDrop.dispose();
          beforeStructuralChange.dispose();
          afterStructuralChange.dispose();
        },
      };

      railSnapDisposableRef.current = installRailSnap(api);
      // Dockview pins the overflow list to the click point; re-anchor to ∨ N.
      overflowPopoverDisposableRef.current = installOverflowPopoverAnchor();

      // Debounced persistence — a drag gesture fires many layout-change events.
      saveDisposableRef.current = api.onDidLayoutChange(() => {
        // Opening the first document tab (or closing the last) is what raises
        // and drops a tabbed area's strip; dockview has no notion of that rule,
        // so re-derive it here.
        applyTabStripVisibility(api);
        applyAreaViewClasses(api);
        // Module TabHost intents may change area visibility without going
        // through `toggleArea`; mirror those changes back to shell controls.
        syncOpenAreas(api);
        // Responsive visibility is transient presentation state. Saving it
        // would make a bar auto-hidden in a narrow window look user-hidden
        // after the next wide-window launch.
        if (responsiveLayoutMutationRef.current) {
          return;
        }
        let serialized: string;
        try {
          // Capture the latest user layout at event time. A later responsive
          // hide must not leak into a pending debounced write.
          serialized = JSON.stringify(stripEditorInstanceTabs(api.toJSON()));
        } catch {
          return;
        }
        if (saveTimerRef.current !== undefined) {
          clearTimeout(saveTimerRef.current);
        }
        pendingLayoutRef.current = serialized;
        saveTimerRef.current = setTimeout(() => {
          pendingLayoutRef.current = undefined;
          try {
            storage?.setItem(LAYOUT_STORAGE_KEY, serialized);
          } catch {
            // Best-effort; ignore storage quota / serialization errors.
          }
        }, 250);
      });
    },
    [syncOpenAreas],
  );

  // Cleanup disposables on unmount.
  useEffect(() => {
    const flushPendingLayout = (): void => {
      const pending = pendingLayoutRef.current;
      if (pending === undefined) {
        return;
      }
      if (saveTimerRef.current !== undefined) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = undefined;
      }
      pendingLayoutRef.current = undefined;
      try {
        window.localStorage.setItem(LAYOUT_STORAGE_KEY, pending);
      } catch {
        // Best-effort; ignore storage quota / serialization errors.
      }
    };
    window.addEventListener('pagehide', flushPendingLayout);
    return () => {
      window.removeEventListener('pagehide', flushPendingLayout);
      shellRelayoutGenerationRef.current += 1;
      saveDisposableRef.current?.dispose();
      dragVetoDisposableRef.current?.dispose();
      overlayDisposableRef.current?.dispose();
      structuralSizeDisposableRef.current?.dispose();
      railSnapDisposableRef.current?.dispose();
      overflowPopoverDisposableRef.current?.dispose();
      if (saveTimerRef.current !== undefined) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  /**
   * Whether each area is enabled by the *effective* schema (`enabled !== false`).
   * Occupancy overlays (unoccupied auxiliaryBar) flow through here, so header
   * toggles hide with the area (架构方案 §5.7).
   */
  const enabledAreas: Record<ToggleableArea, boolean> = {
    panel: isAreaEnabled(findAreaConfig(schema, 'panel')),
    statusBar: isAreaEnabled(findAreaConfig(schema, 'statusBar')),
    auxiliaryBar: isAreaEnabled(findAreaConfig(schema, 'auxiliaryBar')),
  };

  // Return a new object each render so React Context consumers re-render when
  // `openAreas` changes (Context uses Object.is comparison on the value).
  // `toggle` is a stable callback (useCallback) so it never causes extra renders.
  const layoutApi: WorkbenchLayoutApi = {
    get dockview() {
      return apiRef.current ?? null;
    },
    openAreas,
    enabledAreas,
    toggle: toggleArea,
    reset: resetLayout,
  };

  return { onReady, layoutApi };
}
